import React, { useCallback, useState, useEffect } from 'react';
import TopBar from '../components/TopBar';
import googleAnalyticsService from '../services/googleAnalytics';
import sentryService from '../services/sentryService';
import userService from '../services/userService';
import UserApprovalModal from '../components/admin/modals/UserApprovalModal';
import AnalyticsTab from '../components/admin/tabs/AnalyticsTab';
import MailjetStatsTab from '../components/admin/tabs/MailjetStatsTab';
import UsersTab from '../components/admin/tabs/UsersTab';
import LogsTab from '../components/admin/tabs/LogsTab';
import ChatbotLogsTab from '../components/admin/tabs/ChatbotLogsTab';
import ChatbotDocumentsTab from '../components/admin/tabs/ChatbotDocumentsTab';
import TicketAdminTab from '../components/admin/tabs/TicketAdminTab';
import EventDetailsModal from '../components/admin/modals/EventDetailsModal';
import TemplatesTab from '../components/admin/tabs/TemplatesTab';
import mailjetService from '../services/mailjetService';
import IntegrationsTab from '../components/admin/tabs/IntegrationsTab';

const Admin = ({ userClaims }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [analyticsData, setAnalyticsData] = useState({
    basicStats: { pageViews: 0, uniqueUsers: 0, avgSessionDuration: '00:00:00', bounceRate: '0%' },
    topPages: [],
    userCountries: [],
    userOrganizations: [],
    deviceTypes: [],
    realTimeData: { activeUsers: 0, currentPageViews: 0 },
    appliedRange: '14m'
  });
  const [analyticsRange, setAnalyticsRange] = useState('14m');
  const [sentryData, setSentryData] = useState({
    projectStats: { totalEvents: 0, totalIssues: 0, resolvedIssues: 0, unresolvedIssues: 0, errorRate: '0%', crashFreeRate: '0%', activeUsers: 0 },
    issues: [],
    events: []
  });
  const [userData, setUserData] = useState({
    users: [],
    pendingUsers: []
  });
  const [loading, setLoading] = useState(false);
  const [sentryLoading, setSentryLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [chatbotLogs, setChatbotLogs] = useState([]);
  const [chatbotLogsLoading, setChatbotLogsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const [mailjetLoading, setMailjetLoading] = useState(false);
  const [mailjetStats, setMailjetStats] = useState({
    basicStats: { sent: 0, opened: 0, clicked: 0, openRate: '0%', clickRate: '0%', bounceRate: '0%' },
    eventBreakdown: [],
    recentMessages: []
  });

  const tabs = [
    { id: 'users', label: 'Users', icon: 'fa-users' },
    { id: 'stats', label: 'Stats', icon: 'fa-chart-bar' },
    { id: 'mailjet', label: 'Mailjet', icon: 'fa-envelope' },
    { id: 'logs', label: 'Logs', icon: 'fa-file-alt' },
    { id: 'chatbot-logs', label: 'ChatBot Logs', icon: 'fa-robot' },
    { id: 'chatbot-documents', label: 'Chatbot Documents', icon: 'fa-file' },
    { id: 'ticket-admin', label: 'Ticket Admin', icon: 'fa-ticket' },
    { id: 'templates', label: 'Email Templates', icon: 'fa-envelope' },
    { id: 'integrations', label: 'Integrations', icon: 'fa-plug' }
  ];

  const loadAnalyticsData = useCallback(async (range = analyticsRange) => {
    setLoading(true);
    try {
      const [historicalData, realTimeData] = await Promise.all([
        googleAnalyticsService.getAnalyticsData(range),
        googleAnalyticsService.getRealTimeData()
      ]);

      setAnalyticsData({ ...historicalData, realTimeData });
    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [analyticsRange]);

  const loadSentryData = useCallback(async () => {
    setSentryLoading(true);
    try {
      await sentryService.initialize();
      
      const [projectStats, issues, events] = await Promise.all([
        sentryService.getProjectStats(),
        sentryService.getIssues(50, 'all'),
        sentryService.getEvents(50, 'all')
      ]);

      setSentryData({
        projectStats,
        issues,
        events
      });
    } catch (error) {
      console.error('Failed to load Sentry data:', error);
    } finally {
      setSentryLoading(false);
    }
  }, []);

  const loadUserData = useCallback(async () => {
    setUserLoading(true);
    try {
      const requesterEmail = userClaims?.email || '';
      const [users, pendingUsers] = await Promise.all([
        userService.getAllUsers(requesterEmail),
        userService.getPendingUsers(requesterEmail)
      ]);

      setUserData({
        users,
        pendingUsers
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setUserLoading(false);
    }
  }, [userClaims?.email]);

  const loadChatbotLogs = useCallback(async () => {
    setChatbotLogsLoading(true);
    try {
      const { logs } = await userService.getActivityLogs({
        action: 'CHATBOT_TURN',
        limit: 100,
        offset: 0
      });
      setChatbotLogs(Array.isArray(logs) ? logs : []);
    } catch (error) {
      console.error('Failed to load ChatBot logs:', error);
    } finally {
      setChatbotLogsLoading(false);
    }
  }, []);

  const loadMailjetStats = useCallback(async () => {
    setMailjetLoading(true);
    try {
      const data = await mailjetService.getStats();
      setMailjetStats(data);
    } catch (error) {
      console.error('Failed to load Mailjet stats:', error);
    } finally {
      setMailjetLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalyticsData(analyticsRange);
    loadSentryData();
    loadUserData();
    loadChatbotLogs();
    loadMailjetStats();
  }, [analyticsRange, loadAnalyticsData, loadSentryData, loadUserData, loadChatbotLogs, loadMailjetStats]);

  const handleUserApproved = (userId) => {
    setShowUserModal(false);
    setSelectedUser(null);
    loadUserData();
  };

  const handleUserRejected = (userId) => {
    setShowUserModal(false);
    setSelectedUser(null);
    loadUserData();
  };

  const handleReviewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'deactivated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'global': return 'bg-blue-100 text-blue-800';
      case 'regional': return 'bg-green-100 text-green-800';
      case 'country-admin': return 'bg-red-100 text-red-800';
      case 'country': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Add the missing handleViewEventDetails function
  const handleViewEventDetails = async (eventId) => {
    try {
      const eventDetails = await sentryService.getEventDetails(eventId);
      setSelectedEvent(eventDetails);
      setShowEventModal(true);
    } catch (error) {
      console.error('Failed to fetch event details:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="admin" userClaims={userClaims} />
      
      <div className="p-4">
        <div className="bg-white shadow-md rounded-lg">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`fa-solid ${tab.icon}`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'stats' && (
              <AnalyticsTab
                analyticsData={analyticsData}
                loading={loading}
                loadAnalyticsData={loadAnalyticsData}
                analyticsRange={analyticsRange}
                setAnalyticsRange={setAnalyticsRange}
              />
            )}

            {activeTab === 'mailjet' && (
              <MailjetStatsTab
                stats={mailjetStats}
                loading={mailjetLoading}
                onRefresh={loadMailjetStats}
              />
            )}

            {activeTab === 'users' && (
              <UsersTab
                userData={userData}
                userLoading={userLoading}
                loadUserData={loadUserData}
                handleReviewUser={handleReviewUser}
                getStatusColor={getStatusColor}
                getAccessLevelColor={getAccessLevelColor}
                userClaims={userClaims}
              />
            )}

            {activeTab === 'logs' && (
              <LogsTab
                sentryData={sentryData}
                sentryLoading={sentryLoading}
                loadSentryData={loadSentryData}
                handleViewEventDetails={handleViewEventDetails}
                formatTimestamp={formatTimestamp}
                getLevelColor={getLevelColor}
              />
            )}

            {activeTab === 'chatbot-logs' && (
              <ChatbotLogsTab
                chatbotLogs={chatbotLogs}
                chatbotLogsLoading={chatbotLogsLoading}
                loadChatbotLogs={loadChatbotLogs}
              />
            )}

            {activeTab === 'chatbot-documents' && (
              <ChatbotDocumentsTab />
            )}

            {activeTab === 'ticket-admin' && (
              <TicketAdminTab countryCode={userClaims.countryCode} />
            )}

            {activeTab === 'templates' && (
              <TemplatesTab />
            )}

            {activeTab === 'integrations' && (
              <IntegrationsTab userClaims={userClaims} />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEventModal && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setShowEventModal(false)}
          formatTimestamp={formatTimestamp}
          getLevelColor={getLevelColor}
        />
      )}

      {showUserModal && selectedUser && (
        <UserApprovalModal
          user={selectedUser}
          requesterEmail={userClaims?.email}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onApproved={handleUserApproved}
          onRejected={handleUserRejected}
        />
      )}
    </div>
  );
};

export default Admin; 